import BaseAPI, { MAX_LIST_LIMIT, querySuffix } from "./BaseAPI";
import { store } from "@/store";
import { getAPIUrlV2 } from "../apiConfig";
import { AdminOverrideError } from "../Errors.js";
import { userRoute } from "../routes";

export default class UserAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, userRoute, "user");
    }

    async Add(data) {
        const result = await this._request("post", this._url(), data);
        this._emit("add", result.id);
        return result;
    }

    async List(params = {}) {
        return super.List({ limit: MAX_LIST_LIMIT, ...params });
    }

    async GetByID(id) {
        if (!id || id == import.meta.env.VITE_ADMIN_ID) {
            throw AdminOverrideError;
        }
        return this._request("get", this._url(`/${id}`));
    }

    async GetMe() {
        return this._request("get", this._url("/me"));
    }

    async GetStatusByID(id, options = {}) {
        if (!id || id == import.meta.env.VITE_ADMIN_ID) {
            throw AdminOverrideError;
        }
        return this._request("get", this._url(`/${id}/status`), null, options);
    }

    async ListStatuses(params = {}, options = {}) {
        return this._request(
            "get",
            `${getAPIUrlV2("user-statuses")}${querySuffix(params)}`,
            null,
            options
        );
    }

    async SetActiveStatus(active) {
        const userId = store.getters.userID();
        if (!userId) return;
        if (userId == import.meta.env.VITE_ADMIN_ID) return;
        const userData = active
            ? { active_status: active, last_active: Date.now() }
            : { active_status: active };
        return this._request("patch", this._url("/status"), userData, { skipSentry: true });
    }

    async SetLegal(legal) {
        return this._request("patch", this._url("/legal"), legal);
    }

    async Update(id, data) {
        const result = await this._request("put", this._url(`/${id}`), data, { skipSentry: Boolean(data?.current_password) });
        this._emit("update", id);
        return result;
    }

    async SetPin(pin, currentPassword, idempotencyKey) {
        const result = await this._request("post", this._url("/pin"), { pin, current_password: currentPassword }, { skipSentry: true, headers: { "Idempotency-Key": idempotencyKey } });
        this._emit("add", result.id);
        return result;
    }

    async SetRate(id, data) {
        const result = await this._request("post", this._url(`/${id}/rate`), data);
        // Keep rate refresh separate from the subsequent details update.
        // The initiating form owns operation feedback.
        this._emit("rate-update", id);
        return result;
    }

    async Delete(id) {
        const result = await this._request("delete", this._url(`/${id}`));
        this._emit("delete", id);
        return result;
    }

    async Archive(id) {
        const result = await this._request(
            "post",
            `${getAPIUrlV2("control/users")}/mark-archived?id=${id}`
        );
        this._emit("archive", id);
        return result;
    }

    async Deactivate(id) {
        const result = await this._request(
            "post",
            `${getAPIUrlV2("control/users")}/deactivate?id=${id}`
        );
        this._emit("deactivate", id);
        return result;
    }

    async SetFirebaseToken(id, token) {
        return this._request(
            "post",
            `${getAPIUrlV2("control/users")}/set-firebase-token?id=${id}&token=${token}`
        );
    }

    async ListNotificationDevices(installation, options = {}) {
        return this._request("get", this._url("/me/notification-devices"), null, {
            ...options,
            headers: { ...options.headers, "X-Notification-Installation": installation }
        });
    }
    async RegisterNotificationDevice(data, options = {}) {
        return this._request(
            "put",
            this._url("/me/notification-devices/registration"),
            data,
            options
        );
    }
    async ReportNotificationPermission(data, options = {}) {
        return this._request(
            "put",
            this._url("/me/notification-devices/permission"),
            data,
            options
        );
    }
    async DetachNotificationDevice(data, options = {}) {
        return this._request("put", this._url("/me/notification-devices/detach"), data, options);
    }
    async UpdateNotificationDevice(id, data) {
        return this._request(
            "patch",
            this._url(`/me/notification-devices/${encodeURIComponent(id)}`),
            data
        );
    }
    async RemoveNotificationDevice(id) {
        return this._request(
            "delete",
            this._url(`/me/notification-devices/${encodeURIComponent(id)}`)
        );
    }
    async TestNotificationDevice(id, installation, idempotencyKey) {
        return this._request(
            "post",
            this._url(`/me/notification-devices/${encodeURIComponent(id)}/test`),
            {},
            {
                headers: {
                    "X-Notification-Installation": installation,
                    "Idempotency-Key": idempotencyKey
                }
            }
        );
    }

    async SetPassword(id, password) {
        return this._request("put", this._url(`/${id}/password`), { password });
    }
}
